import {
  MICROSOFT_SERVICES,
  MicrosoftService,
} from '@api/api/alfred-integrations';
import { AuthError } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { config } from '@terminal/config';
import {
  deleteMicrosoftDisconnect,
  postMicrosoftConnect,
} from '@terminal/services/alfred-intergations/alfred-integrations-service';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { useEffect } from 'react';
import { AppCardService } from '../AppCard';
import { useConnectionToast } from './useConnectionToast';

export const isValidMicrosoftServiceName = (
  serviceName?: string
): serviceName is MicrosoftService => {
  return (
    typeof serviceName === 'string' &&
    MICROSOFT_SERVICES.some(s => s === serviceName)
  );
};

export const PENDING_SERVICE_NAME_KEY = 'pending_service_name_key';
const PERSONAL_ACCOUNT_TENANT_ID = '9188040d-6c67-4c5b-b112-36a304b66dad';
const LOGIN_FAILED_MESSAGE = 'Microsoft login failed';
const CONNECTION_FAILED_MESSAGE = 'Microsoft connection failed';
const personalCommonScopes = [
  'https://graph.microsoft.com/User.Read',
  'offline_access',
];
const personalScopeMaps = {
  onedrive: [
    'https://graph.microsoft.com/Files.Read.All',
    ...personalCommonScopes,
  ],
  outlook: ['https://graph.microsoft.com/Mail.Read', ...personalCommonScopes],
  onenote: ['https://graph.microsoft.com/Notes.Read', ...personalCommonScopes],
  teams: [
    'https://graph.microsoft.com/Chat.Read',
    'https://graph.microsoft.com/ChannelMessage.Read.All',
    ...personalCommonScopes,
  ],
};

interface UseMicrosoftConnectProps {
  services: AppCardService[];
  isLoadingServices: boolean;
  refetchServices: () => void;
}

export const useMicrosoftConnect = ({
  services,
  isLoadingServices,
  refetchServices,
}: UseMicrosoftConnectProps) => {
  const { accounts, instance } = useMsal();

  const {
    trackMicrosoftServiceConnected,
    trackMicrosoftServiceDisconnected,
    trackMicrosoftServiceConnectionError,
  } = usePosthogEvents();

  const showConnectionToast = useConnectionToast();

  const connectMicrosoft = async (serviceName: MicrosoftService) => {
    localStorage.setItem(PENDING_SERVICE_NAME_KEY, serviceName);

    try {
      await instance.loginRedirect({
        scopes: config.microsoft.scopes,
        // A single token request can only target one resource, so the Graph
        // scopes are pre-consented here instead of requested as a token.
        extraScopesToConsent: personalScopeMaps[serviceName] ?? [],
        redirectUri: config.microsoft.config.auth.redirectUri,
        prompt: 'select_account',
      });
    } catch (error) {
      localStorage.removeItem(PENDING_SERVICE_NAME_KEY);
      const message =
        error instanceof AuthError ? error.errorMessage : LOGIN_FAILED_MESSAGE;
      trackMicrosoftServiceConnectionError(
        serviceName,
        POSTHOG_TRACKING_SOURCES.ALFRED_CONNECTION_APPS_PANEL,
        new Error(message)
      );
      showConnectionToast({ serviceName, status: 'error', message });
    }
  };

  const disconnectMicrosoft = async (
    serviceName: MicrosoftService,
    deleteConversations: boolean
  ) => {
    const res = await deleteMicrosoftDisconnect(serviceName);

    if (res.data) {
      trackMicrosoftServiceDisconnected(
        serviceName,
        POSTHOG_TRACKING_SOURCES.ALFRED_CONNECTION_APPS_PANEL
      );
      refetchServices();
      showConnectionToast({
        action: 'disconnect',
        serviceName,
        status: 'success',
        deleteConversations,
      });
      return true;
    }

    return false;
  };

  useEffect(() => {
    if (!isLoadingServices) {
      const serviceName =
        localStorage.getItem(PENDING_SERVICE_NAME_KEY) ?? undefined;

      instance
        .handleRedirectPromise()
        .then(async redirectResponse => {
          const account = redirectResponse?.account;

          if (isValidMicrosoftServiceName(serviceName) && account) {
            const isPersonalAccount =
              account.tenantId === PERSONAL_ACCOUNT_TENANT_ID;

            if (isPersonalAccount) {
              // Acquire graph token silently for personal account
              await instance.acquireTokenSilent({
                scopes: personalScopeMaps[serviceName],
                account,
              });
            }

            const tokenResponse = await instance.acquireTokenSilent({
              scopes: config.microsoft.scopes,
              account,
            });
            const accessToken = tokenResponse.accessToken;

            const connectedServices = services.flatMap(service =>
              service.connected && isValidMicrosoftServiceName(service.type)
                ? [service.type]
                : []
            );

            const res = await postMicrosoftConnect({
              accessToken: accessToken,
              extra: {
                tenantId: account.tenantId,
              },
              services: Array.from(
                new Set([serviceName, ...connectedServices])
              ),
            });

            const errorMessage =
              res.error?.message ?? CONNECTION_FAILED_MESSAGE;

            if (res.data?.connected) {
              trackMicrosoftServiceConnected(
                serviceName,
                POSTHOG_TRACKING_SOURCES.ALFRED_CONNECTION_APPS_PANEL
              );
              refetchServices();
            } else {
              trackMicrosoftServiceConnectionError(
                serviceName,
                POSTHOG_TRACKING_SOURCES.ALFRED_CONNECTION_APPS_PANEL,
                new Error(errorMessage)
              );
            }

            showConnectionToast({
              serviceName,
              status: res.data?.connected ? 'success' : 'error',
              message: res.data?.connected ? '' : errorMessage,
            });
          }
        })
        .catch(error => {
          console.error(error);
          if (isValidMicrosoftServiceName(serviceName)) {
            const message =
              error instanceof AuthError
                ? error.errorMessage
                : CONNECTION_FAILED_MESSAGE;
            trackMicrosoftServiceConnectionError(
              serviceName,
              POSTHOG_TRACKING_SOURCES.ALFRED_CONNECTION_APPS_PANEL,
              new Error(message)
            );
            showConnectionToast({
              serviceName: serviceName,
              status: 'error',
              message,
            });
          }
        })
        .finally(() => {
          localStorage.removeItem(PENDING_SERVICE_NAME_KEY);
        });
    }
  }, [accounts, instance, isLoadingServices, services]);

  return { connectMicrosoft, disconnectMicrosoft };
};
